import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import { useSettings } from "@/hooks/SettingsContext";
import { trackPlanVisitClick } from "@/lib/analytics";

const LINKS = [
  { to: "/im-new", label: "New Here" },
  { to: "/about", label: "About" },
];

const LINKS_AFTER = [
  { to: "/sermons", label: "Messages" },
  { to: "/resources", label: "Resources" },
];

const MINISTRIES = [
  { to: "/kids", label: "VC Kids" },
  { to: "/lifeline-youth", label: "Lifeline Youth" },
  { to: "/rally-point", label: "The Rally Point" },
  { to: "/in-home", label: "Clay Groups" },
];

const MOBILE_LINKS = [
  { to: "/", label: "Home" },
  { to: "/im-new", label: "New Here" },
  { to: "/about", label: "About" },
  { to: "/vision", label: "Vision" },
  { to: "/sermons", label: "Messages" },
  { to: "/resources", label: "Resources" },
  { to: "/events", label: "Events" },
  { to: "/prayer", label: "Prayer" },
  { to: "/give", label: "Give" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const isAdmin = location.pathname.startsWith("/admin");
  const launch = settings?.launch_mode !== false;
  const ctaLabel = launch ? "PLAN YOUR VISIT" : "PLAN YOUR VISIT";
  const ministryActive = MINISTRIES.some((mn) => location.pathname.startsWith(mn.to));
  const ctaTarget = launch ? { path: "/", id: "service-1" } : { path: "/im-new", id: "plan-your-visit" };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  if (isAdmin) return null;

  const goPlanVisit = (e) => {
    e.preventDefault();
    trackPlanVisitClick();
    setOpen(false);
    if (location.pathname === ctaTarget.path) {
      document.getElementById(ctaTarget.id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(ctaTarget.path);
      setTimeout(() => document.getElementById(ctaTarget.id)?.scrollIntoView({ behavior: "smooth" }), 500);
    }
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
        scrolled ? "bg-navy/95 shadow-lift backdrop-blur-xl" : "bg-navy"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-5 py-2.5 sm:px-8" aria-label="Main navigation">
        <Link to="/" aria-label="Victory Chapel home" data-testid="nav-home-link">
          <Logo variant="light" size="nav" />
        </Link>

        <ul className="hidden items-center gap-5 lg:flex">
          {LINKS.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-semibold tracking-wide transition-colors duration-200 hover:text-white ${
                    isActive ? "text-white underline decoration-victory decoration-2 underline-offset-8" : "text-white/70"
                  }`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
          <li className="group relative">
            <button type="button" data-testid="nav-ministries-toggle" aria-haspopup="true"
                    className={`flex items-center gap-1 text-sm font-semibold tracking-wide transition-colors duration-200 group-hover:text-white ${ministryActive ? "text-white underline decoration-victory decoration-2 underline-offset-8" : "text-white/70"}`}>
              Ministries <ChevronDown size={14} aria-hidden="true" className="transition-transform duration-200 group-hover:rotate-180" />
            </button>
            <ul className="invisible absolute left-0 top-full z-50 mt-3 w-60 rounded-2xl border border-white/10 bg-navy p-2 opacity-0 shadow-lift transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100" data-testid="nav-ministries-menu">
              {MINISTRIES.map((mn) => (
                <li key={mn.to}>
                  <NavLink to={mn.to} data-testid={`nav-ministry-${mn.to.slice(1)}`}
                           className={({ isActive }) => `block rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-white/10 text-white" : "text-white/75 hover:bg-white/5 hover:text-white"}`}>
                    {mn.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </li>
          {LINKS_AFTER.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-semibold tracking-wide transition-colors duration-200 hover:text-white ${
                    isActive ? "text-white underline decoration-victory decoration-2 underline-offset-8" : "text-white/70"
                  }`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <a
            href={`${ctaTarget.path}#${ctaTarget.id}`}
            onClick={goPlanVisit}
            data-testid="nav-plan-visit-cta"
            className="hidden rounded-full bg-victory px-6 py-2.5 font-display text-sm font-700 tracking-wide text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-victory-dark hover:shadow-lift sm:inline-block"
          >
            {ctaLabel}
          </a>
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/5 text-white lg:hidden"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="max-h-[calc(100dvh-10rem)] overflow-y-auto overscroll-contain border-t border-white/10 bg-navy px-5 pb-8 pt-4 shadow-lift lg:hidden"
             data-testid="mobile-menu" data-lenis-prevent>
          <ul className="flex flex-col gap-1">
            {MOBILE_LINKS.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className={({ isActive }) =>
                    `block rounded-xl px-4 py-3.5 font-display text-lg font-700 ${isActive ? "bg-white/10 text-white" : "text-white/80"}`
                  }
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
            <li className="px-4 pb-1 pt-4 text-[11px] font-bold uppercase tracking-[0.25em] text-white/40">Ministries</li>
            {MINISTRIES.map((mn) => (
              <li key={mn.to}>
                <NavLink
                  to={mn.to}
                  data-testid={`mobile-nav-${mn.to.slice(1)}`}
                  className={({ isActive }) =>
                    `block rounded-xl px-4 py-3 pl-8 font-display text-base font-700 ${isActive ? "bg-white/10 text-white" : "text-white/80"}`
                  }
                >
                  {mn.label}
                </NavLink>
              </li>
            ))}
          </ul>
          <a
            href={`${ctaTarget.path}#${ctaTarget.id}`}
            onClick={goPlanVisit}
            data-testid="mobile-plan-visit-cta"
            className="mt-4 block rounded-full bg-victory px-6 py-4 text-center font-display text-base font-700 tracking-wide text-white"
          >
            {ctaLabel}
          </a>
        </div>
      )}
    </header>
  );
}
